import { useMutation } from "@tanstack/react-query";
import {
  useRebarMaterials,
  useAddRebarMaterials,
  useDeleteRebarMaterials,
} from "@api/rebarMaterial";
import {
  useModelFamilyMaterials,
  useAddModelFamilyMaterial,
  useDeleteModelFamilyMaterial,
} from "@api/nonModelledFamily";
import {
  useBenchmarkModelTypeData,
  useAddBenchmarkModelType,
  useDeleteBenchmarkModelType,
  useUpdateBenchmarkModelType,
} from "@api/modelTypeMetadata";
import {
  useAllBenchmarkCatalogsWithValues,
  useCreateBenchmarkCatalog,
  useDeleteBenchmarkCatalog,
  useUpdateBenchmarkCatalog,
} from "@api/benchmarkCatalog";
import {
  useScenarioTypeData,
  useAddScenarioType,
  useUpdateScenarioType,
  useDeleteScenarioType,
} from "@api/scenarioTypeMetadata";

type MetadataType = "rebar-material" | "model-family" | "benchmark-model-type" | "benchmark-catalog" | "scenario-type" | string;

export type DeleteInput = { id: string; value: string };

export interface MetadataApisByTypeReturn {
  useData: any;
  useMetadataAdd: any;
  useMetadataDelete: any;
  useMetadataUpdate: any;
}

const noOp = { mutationFn: async () => undefined };

/**
 * Strategy-based hook that maps a Meta Data Management type to the correct CRUD hooks.
 * - "rebar-material"       → rebarMaterial   (update is no-op).
 * - "model-family"         → nonModelledFamily (update is no-op).
 * - "benchmark-model-type" → modelTypeMetadata.
 * - "benchmark-catalog"    → benchmarkCatalog (full CRUD; UI handled by BenchmarkCatalogDialog).
 *
 * All strategy branches call their hooks unconditionally (React hooks rule)
 * but only the active branch has its query `enabled`. The strategy map then picks
 * the right branch.
 *
 * Delete mutations are wrapped with adapters so every strategy accepts a uniform
 * {@link DeleteInput} shape — the adapter picks the field the underlying API needs.
 */
export function useMetadataApisByType(type: string): MetadataApisByTypeReturn {
  const active = type as MetadataType;

  const rebarDelete = useDeleteRebarMaterials();
  const modelFamilyDelete = useDeleteModelFamilyMaterial();
  const benchmarkModelTypeDelete = useDeleteBenchmarkModelType();
  const catalogDelete = useDeleteBenchmarkCatalog();
  const scenarioTypeDelete = useDeleteScenarioType();

  // Shared no-op mutation for strategies that lack a particular CRUD operation.
  const noopMutation = useMutation(noOp);

  const rebar = {
    useData: useRebarMaterials({ enabled: active === "rebar-material" }),
    useMetadataAdd: useAddRebarMaterials(),
    useMetadataDelete: {
      ...rebarDelete,
      mutateAsync: ({ value }: DeleteInput) => rebarDelete.mutateAsync({ value }),
    },
    useMetadataUpdate: noopMutation,
  };

  const modelFamily = {
    useData: useModelFamilyMaterials({ enabled: active === "model-family" }),
    useMetadataAdd: useAddModelFamilyMaterial(),
    useMetadataDelete: {
      ...modelFamilyDelete,
      mutateAsync: ({ id }: DeleteInput) => modelFamilyDelete.mutateAsync({ id }),
    },
    useMetadataUpdate: noopMutation,
  };

  const benchmarkModelType = {
    useData: useBenchmarkModelTypeData({ enabled: active === "benchmark-model-type" }),
    useMetadataAdd: useAddBenchmarkModelType(),
    useMetadataDelete: {
      ...benchmarkModelTypeDelete,
      mutateAsync: ({ id }: DeleteInput) => benchmarkModelTypeDelete.mutateAsync({ id }),
    },
    useMetadataUpdate: useUpdateBenchmarkModelType(),
  };

  const benchmarkCatalog = {
    useData: useAllBenchmarkCatalogsWithValues({ enabled: active === "benchmark-catalog" }),
    useMetadataAdd: useCreateBenchmarkCatalog(),
    useMetadataDelete: {
      ...catalogDelete,
      mutateAsync: ({ id }: DeleteInput) => catalogDelete.mutateAsync(id),
    },
    useMetadataUpdate: useUpdateBenchmarkCatalog(),
  };

  const scenarioType = {
    useData: useScenarioTypeData({ enabled: active === "scenario-type" }),
    useMetadataAdd: useAddScenarioType(),
    useMetadataDelete: {
      ...scenarioTypeDelete,
      mutateAsync: ({ id }: DeleteInput) => scenarioTypeDelete.mutateAsync({ id }),
    },
    useMetadataUpdate: useUpdateScenarioType(),
  };

  // Fallback for any type not yet registered (keeps hooks unconditional).
  const fallback: MetadataApisByTypeReturn = {
    useData: { data: undefined, isFetching: false, isPending: false, refetch: () => {} },
    useMetadataAdd: noopMutation,
    useMetadataDelete: {
      ...noopMutation,
      mutateAsync: async () => undefined,
    },
    useMetadataUpdate: noopMutation,
  };

  const strategies: Record<string, MetadataApisByTypeReturn> = {
    "rebar-material": rebar,
    "model-family": modelFamily,
    "benchmark-model-type": benchmarkModelType,
    "benchmark-catalog": benchmarkCatalog,
    "scenario-type": scenarioType,
  };

  return strategies[active] ?? fallback;
}
